import { computed, inject, Injectable, signal } from '@angular/core';
import { CompanyClient } from 'src/app/trip-calculator/models/companyClients';
import { CurrentUserService } from './current-user.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CompanyClientService {
  currentUserService = inject(CurrentUserService);
  private http = inject(HttpClient);

  private _companyClients = signal<CompanyClient[] | null>(null);
  companyClients = computed(() => this._companyClients() ?? []);
  isLoaded = computed(() => this._companyClients() !== null);

  isLoading = signal(false);
  error = signal<string | null>(null);

  /**
   * Load clients from API, or return cached value if already loaded.
   * For super admins, this respects the X-Acting-Company-Id header.
   */
  loadClients(): void {
    const cached = this._companyClients();
    if (cached !== null) {
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);
    this.http
      .get<CompanyClient[]>(`${environment.apiUrl}/company-client`)
      .subscribe({
        next: (clients) => {
          this._companyClients.set(clients);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this._companyClients.set([]);
          this.error.set(err.message ?? 'Failed to load clients');
          console.error(err);
        },
      });
  }

  refreshClients(): void {
    this._companyClients.set(null);
    this.loadClients();
  }

  getCompanyClientsFromIds(ids: string[]) {
    return this.companyClients().filter((companyClient) =>
      ids.includes(companyClient.id),
    );
  }

  /**
   * `publicId` is always a plain string here (never omitted/undefined) —
   * deliberately, so a PUT body can't have the key silently dropped by
   * JSON.stringify. An empty string is what tells the backend "clear it".
   */
  createClient(name: string, publicId: string = '') {
    return this.http
      .post<CompanyClient>(`${environment.apiUrl}/company-client/`, {
        name,
        publicId,
      })
      .pipe(
        tap((newClient) => {
          this._companyClients.update((clients) => [
            ...(clients ?? []),
            newClient,
          ]);
        }),
      );
  }

  updateClient(
    clientId: string,
    client: { name: string; publicId: string },
  ): Observable<CompanyClient> {
    return this.http
      .put<CompanyClient>(
        `${environment.apiUrl}/company-client/${clientId}`,
        client,
      )
      .pipe(
        tap((updatedClient) => {
          this._companyClients.update((clients) =>
            (clients ?? []).map((c) => (c.id === clientId ? updatedClient : c)),
          );
        }),
      );
  }

  /**
   * Live check for the "Greentripper Public ID" field — used by the form's
   * async validator so the user finds out before clicking Save, not after.
   */
  checkPublicId(
    publicId: string,
  ): Observable<{ valid: boolean; code?: string }> {
    return this.http.get<{ valid: boolean; code?: string }>(
      `${environment.apiUrl}/company-client/validate-public-id`,
      { params: { publicId } },
    );
  }

  uploadLogo(
    clientId: string,
    logo: Blob,
  ): Observable<{ success: boolean; logoUrl: string }> {
    const formData = new FormData();
    formData.append('image', logo, 'logo.png');
    return this.http
      .post<{
        success: boolean;
        logoUrl: string;
      }>(`${environment.apiUrl}/company-client/${clientId}/logo`, formData)
      .pipe(
        tap(({ logoUrl }) => {
          this._companyClients.update((clients) =>
            (clients ?? []).map((c) =>
              c.id === clientId ? { ...c, logoUrl } : c,
            ),
          );
        }),
      );
  }

  deleteClient(clientId: string): Observable<void> {
    return this.http
      .delete<void>(`${environment.apiUrl}/company-client/${clientId}`)
      .pipe(
        tap(() => {
          this._companyClients.update((clients) =>
            (clients ?? []).filter((c) => c.id !== clientId),
          );
        }),
      );
  }
}
